import { CATEGORY_LABELS, DEFAULT_DEVICE_IDS, DEVICES } from "../data/devices";
import { buildShareUrl, copyText, downloadHtml, formatBytes } from "../core/share";
import type {
  PreviewViewport,
  ViewportCategory,
  RuntimeOptions,
  MountOptions,
  Orientation,
  StudioHandle,
  ViewMode,
  ZoomLevel,
} from "../types";
import { DeviceGrid } from "./DeviceGrid";
import { EditorPane } from "./EditorPane";
import { DESKTOP_VIEWPORTS, customViewports } from "../data/viewports";
import { workspaceControls } from "./WorkspaceControls";
import { TemplateGallery } from "./TemplateGallery";
import { icons } from "./icons";

interface StudioState {
  html: string;
  filename: string;
  selectedIds: Set<string>;
  category: ViewportCategory | "all";
  search: string;
  mode: ViewMode;
  orientation: Orientation;
  zoom: ZoomLevel;
  focusId: string | null;
  pickerOpen: boolean;
  compareIds: string[];
}

const FAMILY_LABELS: Record<ViewportCategory, string> = { ...CATEGORY_LABELS, desktop: "Desktop", custom: "Custom" };

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap";

const SHARE_WARN_BYTES = 48 * 1024;

export function ensureFonts(): void {
  if (document.querySelector(`link[data-hdp-fonts]`)) return;
  const pre1 = document.createElement("link");
  pre1.rel = "preconnect";
  pre1.href = "https://fonts.googleapis.com";
  const pre2 = document.createElement("link");
  pre2.rel = "preconnect";
  pre2.href = "https://fonts.gstatic.com";
  pre2.crossOrigin = "anonymous";
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = FONT_HREF;
  link.dataset.hdpFonts = "true";
  document.head.append(pre1, pre2, link);
}

function sanitizeFilename(name: string): string {
  const trimmed = name.trim().replace(/[\\/:*?"<>|]+/g, "-").slice(0, 80);
  if (!trimmed) return "Untitled.html";
  return /\.html?$/i.test(trimmed) ? trimmed : `${trimmed}.html`;
}

export function createStudio(host: HTMLElement, options: MountOptions = {}): StudioHandle {
  ensureFonts();

  const custom = customViewports(options.customViewports);
  const catalog: PreviewViewport[] = [
    ...DEVICES.filter(device => !options.categories?.length || options.categories.includes(device.category)),
    ...DESKTOP_VIEWPORTS, ...custom,
  ];
  function selectIds(raw?: string[]): string[] {
    if (raw === undefined) return DEFAULT_DEVICE_IDS.filter(id => catalog.some(d => d.id === id));
    return catalog.filter(device => raw.includes(device.id) || raw.includes(device.category)).map(device => device.id);
  }
  const initialSelected = selectIds(options.devices);
  const initialCompare = options.compareIds ?? initialSelected.slice(0, 3);
  function validateCompare(ids: string[], selected: Set<string>): void {
    if (ids.length < 2 || ids.length > 3 || new Set(ids).size !== ids.length || ids.some(id => !selected.has(id))) {
      throw new Error("Choose 2–3 distinct selected viewports for comparison.");
    }
  }
  if (options.compareIds) validateCompare(options.compareIds, new Set(initialSelected));

  const state: StudioState = {
    html: options.html ?? "",
    filename: sanitizeFilename(options.filename ?? "Untitled.html"),
    selectedIds: new Set(initialSelected),
    category: "all",
    search: "",
    mode: options.mode ?? "grid",
    orientation: options.orientation ?? "portrait",
    zoom: options.zoom ?? "fit",
    focusId: initialSelected[0] ?? catalog[0]?.id ?? null,
    pickerOpen: false,
    compareIds: [...initialCompare],
  };

  host.classList.add("hdp-root");
  host.dataset.theme = options.theme ?? "light";
  host.tabIndex = -1;
  if (options.height) host.style.height = options.height;
  host.innerHTML = "";

  const showChrome = options.showChrome !== false;
  let showEditor = options.showEditor !== false;

  const actionsHtml = `
    <div class="hdp-actions">
      <button type="button" class="hdp-btn" data-embed title="Get embed code">${icons.doc}<span>Embed</span></button>
      <button type="button" class="hdp-btn" data-theme title="Switch color theme">◐<span>Theme</span></button>
      <button type="button" class="hdp-btn" data-act="copy" title="Copy source (Ctrl/Cmd+Shift+C)">${icons.copy}<span>Copy</span></button>
      <button type="button" class="hdp-btn" data-act="share" title="Copy share link">${icons.share}<span>Share</span></button>
      <button type="button" class="hdp-btn hdp-btn--primary" data-act="download" title="Download HTML (Ctrl/Cmd+S)">${icons.download}<span>Download</span></button>
    </div>
  `;

  const topbar = document.createElement("header");
  topbar.className = "hdp-topbar";
  if (showChrome) {
    topbar.innerHTML = `
      <div class="hdp-topbar__left">
        <div class="hdp-brand"><span class="hdp-mark" aria-hidden="true">V</span><strong>Viewport<span> Lab</span><small>RESPONSIVE HTML STUDIO</small></strong></div>
        <nav class="hdp-nav" aria-label="Studio menus">
          <div class="hdp-menu">
            <button type="button" class="hdp-menu__btn" data-open="file">File</button>
            <div class="hdp-menu__list" data-panel="file" hidden>
              <button type="button" data-file="new">${icons.plus} New document</button>
              <button type="button" data-templates>${icons.doc} Sample templates…</button>
              <button type="button" data-file="upload">${icons.upload} Upload HTML…</button>
              <hr>
              <button type="button" data-act="download">${icons.download} Download HTML <kbd>⌘S</kbd></button>
              <button type="button" data-act="copy">${icons.copy} Copy source <kbd>⇧⌘C</kbd></button>
              <button type="button" data-act="share">${icons.share} Copy share link</button>
              <hr>
              <button type="button" data-file="clear">${icons.trash} Clear</button>
            </div>
          </div>
          <div class="hdp-menu">
            <button type="button" class="hdp-menu__btn" data-open="view">View</button>
            <div class="hdp-menu__list" data-panel="view" hidden>
              <button type="button" data-mode="grid">${icons.grid} Grid</button>
              <button type="button" data-mode="focus">${icons.focus} Focus</button>
              <button type="button" data-mode="compare">${icons.compare} Compare</button>
              <hr>
              <button type="button" data-orient="portrait">Portrait</button>
              <button type="button" data-orient="landscape">Landscape</button>
              <hr>
              <button type="button" data-zoom="fit">Zoom to fit</button>
              <button type="button" data-zoom="50">Zoom 50%</button>
              <button type="button" data-zoom="75">Zoom 75%</button>
              <button type="button" data-zoom="100">Zoom 100%</button>
            </div>
          </div>
          <div class="hdp-menu">
            <button type="button" class="hdp-menu__btn" data-open="devices">Devices</button>
            <div class="hdp-menu__list" data-panel="devices" hidden>
              <button type="button" data-cat="all">All devices</button>
              <button type="button" data-cat="iphone">iPhone</button>
              <button type="button" data-cat="ipad">iPad</button>
              <button type="button" data-cat="android-phone">Android phones</button>
              <button type="button" data-cat="android-tablet">Android tablets</button>
              <hr>
              <button type="button" data-picker>Open catalog…</button>
              <button type="button" data-all>Select visible</button>
              <button type="button" data-popular>Popular set</button>
              <button type="button" data-none>Deselect visible</button>
            </div>
          </div>
        </nav>
      </div>
      <div class="hdp-topbar__center">
        <button type="button" class="hdp-filename" data-filename title="Rename file">${icons.doc}<span></span></button>
      </div>
      ${actionsHtml}
    `;
    host.append(topbar);
  }

  const shell = document.createElement("div");
  shell.className = "hdp-shell";
  if (!showEditor) shell.classList.add("hdp-shell--preview");
  host.append(shell);

  let previewTimer = 0;
  let editor: EditorPane | null = null;
  function ensureEditor(): EditorPane {
    if (!editor) {
      editor = new EditorPane((html) => {
        state.html = html;
        window.clearTimeout(previewTimer);
        previewTimer = window.setTimeout(() => grid.setHtml(state.html), 160);
        refreshEmpty(); syncStatus();
        options.onChange?.(html);
        host.dispatchEvent(new CustomEvent("html-change", { detail: { html }, bubbles: true, composed: true }));
      }, state.html, options.editorMode ?? "source", name => { state.filename = sanitizeFilename(name); syncStatus(); });
      shell.prepend(editor.el);
      editor.attachRoot();
    }
    return editor;
  }
  if (showEditor) ensureEditor();

  const workspace = document.createElement("section");
  workspace.className = "hdp-workspace";
  shell.append(workspace);

  const toolbar = document.createElement("div");
  toolbar.className = "hdp-previewbar";
  toolbar.innerHTML = `
    <div class="hdp-tabs" role="group" aria-label="Device family">
      <button type="button" class="is-on" data-cat="all">All</button>
      <button type="button" data-cat="iphone">iPhone</button>
      <button type="button" data-cat="ipad">iPad</button>
      <button type="button" data-cat="android-phone">Android</button>
      <button type="button" data-cat="android-tablet">Tablets</button>
      <button type="button" data-cat="desktop">Desktop</button>
      <button type="button" data-cat="custom">Custom</button>
    </div>
    <label class="hdp-search">
      <span class="visually-hidden">Search devices</span>
      <input type="search" placeholder="Find device">
    </label>
    <div class="hdp-previewbar__opts">
      <div class="hdp-seg" role="group" aria-label="Preview layout">
        <button type="button" class="is-on" data-mode="grid" title="Grid">${icons.grid}</button>
        <button type="button" data-mode="focus" title="Focus">${icons.focus}</button>
        <button type="button" data-mode="compare" title="Compare">${icons.compare}</button>
      </div>
      <label class="hdp-select">
        <span class="visually-hidden">Zoom</span>
        <select data-zoom>
          <option value="fit">Fit</option>
          <option value="50">50%</option>
          <option value="75">75%</option>
          <option value="100">100%</option>
        </select>
      </label>
      <button type="button" class="hdp-iconbtn" data-orient title="Rotate">${icons.rotate}</button>
      <button type="button" class="hdp-btn" data-picker>Catalog <span class="hdp-count" data-count></span></button>
      ${showChrome ? "" : actionsHtml}
    </div>
  `;
  const workspaceHeading = document.createElement("div");
  workspaceHeading.className = "hdp-workspace-heading";
  workspaceHeading.innerHTML = `<div><span class="hdp-eyebrow">02 / PREVIEW</span><h1>Every screen. One canvas.</h1><p>See your HTML across real device dimensions, as you edit.</p></div><div class="hdp-workspace-actions"><button class="hdp-btn" type="button" data-templates>Templates <span class="hdp-count">10</span></button><button class="hdp-btn" type="button" data-toggle-editor>Hide editor</button><button class="hdp-btn" type="button" data-fullscreen>Fullscreen</button></div>`;
  workspace.append(workspaceHeading, toolbar);
  const viewbar = document.createElement("div");
  viewbar.className = "hdp-viewbar";
  viewbar.innerHTML = `<span data-view-summary></span><label class="hdp-select">Active device <select data-focus aria-label="Active device"></select></label>`;
  workspace.append(viewbar);
  const focusSelect = viewbar.querySelector<HTMLSelectElement>("[data-focus]")!;
  focusSelect.addEventListener("change", () => { state.focusId = focusSelect.value; renderGrid(); });

  const compareBar = document.createElement("div");
  compareBar.className = "hdp-compare-controls";
  workspace.append(compareBar);

  const picker = document.createElement("div");
  picker.className = "hdp-picker";
  picker.hidden = true;
  workspace.append(picker);
  const customForm = document.createElement("form");
  customForm.className = "hdp-custom-form";
  customForm.innerHTML = `<strong>Add custom viewport</strong><label>Name<input name="name" required maxlength="80" placeholder="My viewport"></label><label>Width<input name="width" type="number" min="240" max="3840" value="1280" required></label><label>Height<input name="height" type="number" min="240" max="3840" value="800" required></label><button type="submit" class="hdp-btn">Add viewport</button><span role="status" data-custom-error></span>`;
  customForm.hidden = true;
  workspace.append(customForm);
  let nextCustomId = 1;
  customForm.addEventListener("submit", event => {
    event.preventDefault();
    const data = new FormData(customForm);
    while (catalog.some(view => view.id === `custom-${nextCustomId}`)) nextCustomId++;
    try {
      const view = { id: `custom-${nextCustomId++}`, name: String(data.get("name")).trim(), width: Number(data.get("width")), height: Number(data.get("height")) };
      const normalized = customViewports([...custom, view]);
      custom.push(normalized[normalized.length - 1]);
      catalog.push(custom[custom.length - 1]);
      state.selectedIds.add(view.id);
      customForm.querySelector("[data-custom-error]")!.textContent = "Viewport added";
      renderPicker(); renderGrid();
    } catch (error) { customForm.querySelector("[data-custom-error]")!.textContent = (error as Error).message; }
  });

  const stage = document.createElement("div");
  stage.className = "hdp-stage";
  workspace.append(stage);

  const empty = document.createElement("div");
  empty.className = "hdp-empty";
  empty.innerHTML = `
    <span class="hdp-empty__icon">${icons.doc}</span>
    <span class="hdp-eyebrow">YOUR NEXT IDEA STARTS HERE</span>
    <h2>Small screen.<br>Big possibilities.</h2>
    <p>Drop in your HTML and see the whole picture. One workspace for every viewport.</p>
    <div class="hdp-empty__actions">
      <button type="button" class="hdp-btn hdp-btn--primary" data-file="sample">${icons.doc} Explore a sample</button>
      <button type="button" class="hdp-btn" data-templates>Browse 10 templates</button>
      <button type="button" class="hdp-btn" data-file="upload">${icons.upload} Upload HTML</button>

    </div>
    <div class="hdp-empty__details"><span>Mobile, desktop & custom sizes</span><span>Any framework</span><span>Works in your browser</span></div>
  `;
  stage.append(empty);

  const grid = new DeviceGrid((id) => {
    state.focusId = id;
    if (state.mode === "grid") state.mode = "focus";
    syncToolbar();
    renderGrid();
  });
  stage.append(grid.el);

  const statusbar = document.createElement("footer");
  statusbar.className = "hdp-statusbar";
  statusbar.innerHTML = `
    <span data-status-doc></span>
    <span class="hdp-statusbar__sep"></span>
    <span data-status></span>
    <span class="hdp-toast" role="status" aria-live="polite" data-toast hidden></span>
  `;
  host.append(statusbar);

  const searchInput = toolbar.querySelector<HTMLInputElement>("input[type='search']")!;
  const zoomSelect = toolbar.querySelector<HTMLSelectElement>("[data-zoom]")!;
  zoomSelect.value = state.zoom;

  const gallery = new TemplateGallery(template => {
    setEditorVisible(true);
    ensureEditor().loadSample(template.html, `${template.id}.html`);
    toast(`${template.name} loaded`);
  });
  host.append(gallery.el);
  host.querySelectorAll("[data-templates]").forEach(button => button.addEventListener("click", () => { closeMenus(); gallery.open(); }));

  const embedDialog = document.createElement("dialog");
  embedDialog.className = "hdp-embed-dialog";
  embedDialog.innerHTML = `<form method="dialog"><button class="hdp-iconbtn" aria-label="Close embed guide">×</button></form><span class="hdp-eyebrow">TAKE YOUR WORKSPACE ANYWHERE</span><h2>Made to fit your stack.</h2><p>Use this browser snippet in HTML, Laravel, PHP, Django, or any page that serves HTML. It includes your current document.</p><textarea readonly aria-label="Embed code" spellcheck="false"></textarea><div class="hdp-embed-dialog__foot"><span>Shadow DOM styles · Framework independent</span><button type="button" class="hdp-btn hdp-btn--primary" data-copy-embed>Copy embed code</button></div>`;
  host.append(embedDialog);
  host.querySelectorAll("[data-embed]").forEach((button) => button.addEventListener("click", () => {
    const config = JSON.stringify({ html: state.html, filename: state.filename, height: "820px", theme: host.dataset.theme,
      devices: [...state.selectedIds], mode: state.mode, orientation: state.orientation, zoom: state.zoom, showEditor,
      customViewports: custom.map(({ id, name, width, height }) => ({ id, name, width, height })),
      ...(state.compareIds.length >= 2 ? { compareIds: state.compareIds } : {}),
    }, null, 2).replaceAll("<", "\\u003c");
    embedDialog.querySelector("textarea")!.value = `<div id="html-preview"></div>\n<script src="https://aneebji.github.io/html-editor/html-device-preview.umd.js"><\/script>\n<script>\n  HtmlDevicePreview.mount("#html-preview", ${config});\n<\/script>`;
    embedDialog.showModal();
  }));
  embedDialog.querySelector("[data-copy-embed]")!.addEventListener("click", async () => {
    const ok = await copyText(embedDialog.querySelector("textarea")!.value);
    embedDialog.querySelector("[data-copy-embed]")!.textContent = ok ? "Copied!" : "Select code and copy manually";
  });
  host.querySelectorAll("[data-theme]").forEach((button) => button.addEventListener("click", () => {
    host.dataset.theme = host.dataset.theme === "light" ? "dark" : "light";
  }));
  const controls = workspaceControls(shell, workspace, message => toast(message, "warn"));
  function setEditorVisible(visible: boolean): void {
    showEditor = visible;
    if (visible) ensureEditor();
    if (editor) editor.el.hidden = !visible;
    controls.setEditorVisible(visible);
    workspace.querySelector("[data-toggle-editor]")!.textContent = visible ? "Hide editor" : "Show editor";
    refreshEmpty();
  }
  workspace.querySelector("[data-toggle-editor]")!.addEventListener("click", () => setEditorVisible(!showEditor));
  let toastTimer = 0;
  function toast(message: string, tone: "ok" | "warn" = "ok"): void {
    const el = statusbar.querySelector<HTMLElement>("[data-toast]")!;
    el.textContent = message;
    el.dataset.tone = tone;
    el.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      el.hidden = true;
    }, 2400);
  }

  function closeMenus(): void {
    for (const panel of host.querySelectorAll<HTMLElement>("[data-panel]")) panel.hidden = true;
    for (const btn of host.querySelectorAll(".hdp-menu__btn")) btn.classList.remove("is-on");
  }

  host.querySelectorAll<HTMLButtonElement>("[data-open]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const panel = host.querySelector<HTMLElement>(`[data-panel="${btn.dataset.open}"]`);
      const willOpen = Boolean(panel?.hidden);
      closeMenus();
      if (panel && willOpen) {
        panel.hidden = false;
        btn.classList.add("is-on");
      }
    });
  });

  const onDocumentPointer = (e: Event): void => {
    const inMenu = e
      .composedPath()
      .some((n) => n instanceof Element && n.classList.contains("hdp-menu"));
    if (!inMenu) closeMenus();
  };
  document.addEventListener("pointerdown", onDocumentPointer);
  document.addEventListener("click", onDocumentPointer);

  async function doCopy(): Promise<boolean> {
    const ok = await copyText(state.html);
    toast(ok ? "Source copied" : "Copy failed", ok ? "ok" : "warn");
    return ok;
  }

  function doDownload(): void {
    downloadHtml(state.html, state.filename);
    toast(`Downloaded ${state.filename}`);
  }

  async function doShare(): Promise<string> {
    const url = await buildShareUrl(state.html, options.shareBaseUrl ?? "https://aneebji.github.io/html-editor/");
    const ok = await copyText(url);
    const big = new TextEncoder().encode(state.html).length > SHARE_WARN_BYTES;
    if (!ok) toast("Could not copy link", "warn");
    else if (big) toast("Link copied · large document, prefer Download", "warn");
    else toast("Share link copied");
    return url;
  }

  host.querySelectorAll<HTMLButtonElement>("[data-act]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const act = btn.dataset.act;
      if (act === "copy") void doCopy();
      if (act === "download") doDownload();
      if (act === "share") void doShare();
      closeMenus();
    });
  });

  host.querySelectorAll<HTMLButtonElement>("[data-file]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const act = btn.dataset.file;
      if (act === "new") editor?.newDocument();
      if (act === "sample") editor?.loadSample();
      if (act === "upload") editor?.openUpload();
      if (act === "clear") editor?.clear();
      closeMenus();
    });
  });

  const filenameBtn = host.querySelector<HTMLButtonElement>("[data-filename]");
  filenameBtn?.addEventListener("click", () => {
    const next = window.prompt("File name", state.filename);
    if (next == null) return;
    state.filename = sanitizeFilename(next);
    syncStatus();
  });

  host.addEventListener("keydown", (e) => {
    if (e.key === "Escape") { closeMenus(); state.pickerOpen = false; picker.hidden = true; customForm.hidden = true; }
    const meta = e.metaKey || e.ctrlKey;
    if (!meta) return;
    if (e.key.toLowerCase() === "s" && !e.shiftKey) {
      e.preventDefault();
      doDownload();
    } else if (e.key.toLowerCase() === "c" && e.shiftKey) {
      e.preventDefault();
      void doCopy();
    }
  });

  host.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.category = btn.dataset.cat as StudioState["category"];
      closeMenus();
      syncToolbar();
      renderPicker();
      renderGrid();
    });
  });

  host.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.mode = btn.dataset.mode as ViewMode;
      closeMenus();
      syncToolbar();
      renderGrid();
    });
  });

  searchInput.addEventListener("input", () => {
    state.search = searchInput.value;
    renderPicker();
    renderGrid();
  });

  zoomSelect.addEventListener("change", () => {
    state.zoom = zoomSelect.value as ZoomLevel;
    syncToolbar();
    renderGrid();
  });

  host.querySelectorAll<HTMLButtonElement>("button[data-zoom]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.zoom = btn.dataset.zoom as ZoomLevel;
      zoomSelect.value = state.zoom;
      closeMenus();
      syncToolbar();
      renderGrid();
    });
  });

  toolbar.querySelector("[data-orient]")!.addEventListener("click", () => {
    state.orientation = state.orientation === "portrait" ? "landscape" : "portrait";
    syncToolbar();
    renderGrid();
  });

  host.querySelectorAll<HTMLButtonElement>(".hdp-menu__list [data-orient]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.orientation = btn.dataset.orient as Orientation;
      closeMenus();
      syncToolbar();
      renderGrid();
    });
  });

  host.querySelectorAll("[data-picker]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.pickerOpen = !state.pickerOpen;
      picker.hidden = !state.pickerOpen;
      customForm.hidden = !state.pickerOpen;
      renderPicker();
      closeMenus();
    });
  });

  function selectVisible(on: boolean): void {
    for (const d of filteredCatalog()) {
      if (on) state.selectedIds.add(d.id);
      else state.selectedIds.delete(d.id);
    }
    renderPicker();
    renderGrid();
  }

  host.querySelectorAll("[data-all]").forEach((btn) =>
    btn.addEventListener("click", () => {
      selectVisible(true);
      closeMenus();
    }),
  );
  host.querySelectorAll("[data-popular]").forEach((btn) =>
    btn.addEventListener("click", () => {
      state.selectedIds = new Set(
        DEFAULT_DEVICE_IDS.filter((id) => catalog.some((d) => d.id === id)),
      );
      renderPicker();
      renderGrid();
      closeMenus();
    }),
  );
  host.querySelectorAll("[data-none]").forEach((btn) =>
    btn.addEventListener("click", () => {
      selectVisible(false);
      closeMenus();
    }),
  );

  function filteredCatalog(): PreviewViewport[] {
    const q = state.search.trim().toLowerCase();
    return catalog.filter((d) => {
      if (state.category !== "all" && d.category !== state.category) return false;
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        d.brand.toLowerCase().includes(q) ||
        d.id.includes(q)
      );
    });
  }

  function visibleDevices(): PreviewViewport[] {
    return (state.mode === "compare" ? catalog : filteredCatalog()).filter((d) => state.selectedIds.has(d.id));
  }

  function syncStatus(): void {
    const docEl = statusbar.querySelector("[data-status-doc]");
    if (docEl) {
      const bytes = new TextEncoder().encode(state.html).length;
      docEl.textContent = `${state.filename} · ${formatBytes(bytes)} · ${state.html.length.toLocaleString()} chars`;
    }
    const nameEl = filenameBtn?.querySelector("span");
    if (nameEl) nameEl.textContent = state.filename;
  }

  function syncToolbar(): void {
    host.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((btn) => {
      btn.classList.toggle("is-on", btn.dataset.cat === state.category);
    });
    host.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((btn) => {
      btn.classList.toggle("is-on", btn.dataset.mode === state.mode);
    });
    host.querySelectorAll<HTMLButtonElement>("button[data-zoom]").forEach((btn) => {
      btn.classList.toggle("is-on", btn.dataset.zoom === state.zoom);
    });
    host.querySelectorAll<HTMLButtonElement>(".hdp-menu__list [data-orient]").forEach((btn) => {
      btn.classList.toggle("is-on", btn.dataset.orient === state.orientation);
    });
    zoomSelect.value = state.zoom;
    const visible = visibleDevices();
    viewbar.querySelector("[data-view-summary]")!.textContent = `${visible.length} selected viewports · ${state.orientation}`;
    focusSelect.parentElement!.hidden = state.mode !== "focus";
    focusSelect.replaceChildren(...visible.map((device) => new Option(device.name, device.id)));
    focusSelect.value = state.focusId ?? "";
    host.querySelectorAll<HTMLButtonElement>("[data-mode], [data-cat]").forEach((button) => button.setAttribute("aria-pressed", String(button.classList.contains("is-on"))));
    const count = toolbar.querySelector("[data-count]");
    if (count) count.textContent = `${state.selectedIds.size}/${catalog.length}`;
    const status = statusbar.querySelector("[data-status]");
    if (status) {
      const family = state.category === "all" ? "All families" : FAMILY_LABELS[state.category];
      const zoom = state.zoom === "fit" ? "Fit" : `${state.zoom}%`;
      status.textContent = `${state.selectedIds.size} of ${catalog.length} devices · ${family} · ${state.mode} · ${state.orientation} · ${zoom}`;
    }
    renderCompare();
    syncStatus();
  }

  function renderPicker(): void {
    const list = filteredCatalog();
    picker.innerHTML = `
      <div class="hdp-picker__bar">
        <span>${list.length} matching · ${FAMILY_LABELS[state.category as ViewportCategory] ?? "All families"}</span>
        <div>
          <button type="button" class="hdp-btn hdp-btn--sm" data-all>All</button>
          <button type="button" class="hdp-btn hdp-btn--sm" data-popular>Popular</button>
          <button type="button" class="hdp-btn hdp-btn--sm" data-none>None</button>
        </div>
      </div>
      <div class="hdp-picker__list">
        ${list
          .map(
            (d) => `
          <label class="hdp-pick">
            <input type="checkbox" value="${d.id}" ${state.selectedIds.has(d.id) ? "checked" : ""}>
            <span>${escapeHtml(d.name)}</span>
            <em>${d.width}×${d.height}</em>
          </label>`,
          )
          .join("")}
      </div>
    `;
    picker.querySelector("[data-all]")!.addEventListener("click", () => selectVisible(true));
    picker.querySelector("[data-popular]")!.addEventListener("click", () => {
      state.selectedIds = new Set(
        DEFAULT_DEVICE_IDS.filter((id) => catalog.some((d) => d.id === id)),
      );
      renderPicker();
      renderGrid();
    });
    picker.querySelector("[data-none]")!.addEventListener("click", () => selectVisible(false));
    picker.querySelectorAll<HTMLInputElement>("input[type='checkbox']").forEach((box) => {
      box.addEventListener("change", () => {
        if (box.checked) state.selectedIds.add(box.value);
        else state.selectedIds.delete(box.value);
        renderGrid();
      });
    });
  }

  function refreshEmpty(): void {
    const has = Boolean(state.html.trim());
    empty.hidden = has;
    empty.querySelectorAll<HTMLButtonElement>("[data-file]").forEach((button) => { button.hidden = !showEditor; });
    grid.el.hidden = !has;
  }

  function renderCompare(): void {
    compareBar.hidden = state.mode !== "compare";
    compareBar.replaceChildren();
    const selected = catalog.filter(device => state.selectedIds.has(device.id));
    const message = document.createElement("span");
    message.textContent = selected.length < 2 ? "Select at least two viewports in Catalog." : "Compare 2–3 viewports";
    compareBar.append(message);
    for (let index = 0; index < 3; index++) {
      const select = document.createElement("select");
      select.setAttribute("aria-label", `Comparison viewport ${index + 1}`);
      select.append(new Option(index === 2 ? "No third viewport" : "Choose viewport", ""));
      for (const device of selected) {
        const option = new Option(device.name, device.id);
        option.disabled = state.compareIds.includes(device.id) && state.compareIds[index] !== device.id;
        select.append(option);
      }
      select.value = state.compareIds[index] ?? "";
      select.addEventListener("change", () => {
        const ids = [...state.compareIds]; ids[index] = select.value;
        const next = ids.filter(Boolean);
        if (next.length < 2) { toast("Choose at least two comparison viewports", "warn"); renderCompare(); return; }
        state.compareIds = next; renderGrid();
        compareBar.querySelectorAll<HTMLSelectElement>("select")[index]?.focus();
      });
      compareBar.append(select);
    }
  }

  function setOptions(next: RuntimeOptions): void {
    if (next.showEditor !== undefined && typeof next.showEditor !== "boolean") throw new Error("showEditor must be a boolean");
    const selected = next.devices === undefined ? state.selectedIds : new Set(selectIds(next.devices));
    if (next.compareIds !== undefined) validateCompare(next.compareIds, selected);
    const choices: [unknown, unknown[], string][] = [
      [next.theme, ["light", "dark"], "theme"], [next.mode, ["grid", "focus", "compare"], "mode"],
      [next.orientation, ["portrait", "landscape"], "orientation"], [next.zoom, ["fit", "50", "75", "100"], "zoom"],
    ];
    for (const [value, allowed, name] of choices) if (value !== undefined && !allowed.includes(value)) throw new Error(`Invalid ${name}`);
    if (next.theme) host.dataset.theme = next.theme;
    state.selectedIds = selected;
    if (next.devices !== undefined) { state.category = "all"; state.search = ""; searchInput.value = ""; }
    if (next.mode) state.mode = next.mode;
    if (next.orientation) state.orientation = next.orientation;
    if (next.zoom) state.zoom = next.zoom;
    if (next.compareIds) state.compareIds = [...next.compareIds];
    if (next.showEditor !== undefined) setEditorVisible(next.showEditor);
    renderPicker(); renderGrid();
  }

  function renderGrid(): void {
    state.compareIds = state.compareIds.filter(id => state.selectedIds.has(id));
    if (state.compareIds.length < 2) {
      for (const id of state.selectedIds) {
        if (!state.compareIds.includes(id)) state.compareIds.push(id);
        if (state.compareIds.length >= 2) break;
      }
    }
    const devices = visibleDevices();
    if (!state.focusId || !devices.some((d) => d.id === state.focusId)) {
      state.focusId = devices[0]?.id ?? null;
    }
    grid.setView({
      devices,
      mode: state.mode,
      orientation: state.orientation,
      zoom: state.zoom,
      focusId: state.focusId,
      compareIds: state.compareIds,
    });
    grid.setHtml(state.html);
    syncToolbar();
  }

  setEditorVisible(showEditor);
  renderPicker();
  renderGrid();
  refreshEmpty();

  return {
    el: host,
    setOptions,
    setHtml(html: string) {
      window.clearTimeout(previewTimer);
      state.html = html;
      editor?.setValue(html);
      grid.setHtml(html);
      refreshEmpty();
      syncStatus();
    },
    getHtml: () => state.html,
    getFilename: () => state.filename,
    setFilename(name: string) {
      state.filename = sanitizeFilename(name);
      syncStatus();
    },
    download: doDownload,
    copy: doCopy,
    shareUrl: () => buildShareUrl(state.html, options.shareBaseUrl ?? "https://aneebji.github.io/html-editor/"),
    destroy() {
      document.removeEventListener("pointerdown", onDocumentPointer);
      document.removeEventListener("click", onDocumentPointer);
      window.clearTimeout(previewTimer);
      gallery.destroy();
      embedDialog.close();
      window.clearTimeout(toastTimer);
      controls.destroy();
      editor?.destroy();
      grid.destroy();
      host.replaceChildren();
      host.classList.remove("hdp-root");
    },
  };
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
